/* eslint-disable security/detect-unsafe-regex */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import NeuroMessage from '../NeuroMessage';
import { useNeuroMessages } from '../MessageContext';
import getLogger from '@ignitesales/logger-es';
import classnames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { isValidPhoneNumber } from 'libphonenumber-js';

const logger = getLogger({ module: 'FormInput' });

const FormInput = (props) => {
  const {
    disabled,
    errorPositionAfterInput,
    formId,
    initialValue,
    name,
    onChange,
    timeout,
    type = 'text',
    options = [],
  } = props;
  const messageContext = useNeuroMessages();
  const { register, setValue, formState: { errors } } = useFormContext();
  const formValidations = getFormInputValidationsOptions(props);
  const inputFieldProps = register(name, formValidations);

  const componentClass = 'formInput';
  const classes = classnames(componentClass, `${componentClass}--${name}`);

  const msgIdPrefix = formId ? `${formId}.` : '';
  const placeHolderMsgId = messageContext.hasMessage(`${msgIdPrefix}formInput.${name}.placeholder`)
    ? `${msgIdPrefix}formInput.${name}.placeholder`
    : `formInput.${name}.placeholder`;

  const renderError = () => {
    const hasError = errors[name];
    if (hasError) {
      const errorType = hasError?.type;
      const errorMsgId = messageContext.hasMessage(`${msgIdPrefix}formInput.${name}.error.${errorType}`)
        ? `${msgIdPrefix}formInput.${name}.error.${errorType}`
        : `formInput.error.${errorType}`;
      return <NeuroMessage id={errorMsgId} tagName='span' className={`${componentClass}__error`} />;
    } else {
      return null;
    }
  };

  const debounceOnChange = useRef(debounce((v) => onChange({ value: v, name }), timeout));

  const onInputChange = (e) => {
    const { value } = e.target;

    inputFieldProps.onChange(e);
    if (onChange) {
      timeout ? debounceOnChange.current(value) : onChange({ value, name });
    }
  };

  const handleOnBlur = (e) => {
    const { value } = e.target;
    const newValue = value.trim();
    setValue(name, newValue);
  };

  useEffect(() => {
    if(initialValue) {
      logger.debug(`Setting initial value for field ${name}`, initialValue);
      setValue(name, initialValue);
    }
  }, []);

  return (
    <div key={name} className={classes}>
      <label htmlFor={name}>
        <NeuroMessage id={`${msgIdPrefix}formInput.${name}`} tagName="span" />
        { !errorPositionAfterInput && renderError() }
      </label>
      { type !== 'dropdown' ?
        <input
          id={`${msgIdPrefix}${name}`}
          type={type}
          name={name}
          placeholder={ messageContext.formatIntlMessage({ id: placeHolderMsgId }) }
          disabled={disabled}
          { ...inputFieldProps }
          onChange={onInputChange}
          onBlur={handleOnBlur}
        /> :
        <select
          name={name}
          id={`${msgIdPrefix}${name}`}
          disabled={disabled}
          { ...inputFieldProps }
          onChange={onInputChange}
        >
          { options.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      }
      { errorPositionAfterInput && renderError() }
    </div>
  );
};

const getFormInputValidationsOptions = (props) => {
  const result = {};
  if(props?.required) result['required'] = true;
  if(props?.max && props.max > 0) result['max'] = props.max;
  if(props?.min && props.min > 0) result['min'] = props.min;
  if(props?.minLength && props.minLength > 0) result['minLength'] = props.minLength;
  if(props?.minLength && props.minLength > 0) result['minLength'] = props.minLength;
  if(props?.matchPattern) result['pattern'] = props.matchPattern;
  if(props?.isEmail) result['pattern'] = /^\w+([.-]?\w+)+@\w+([.:]?\w+)+(\.[a-zA-Z0-9]{2,3})+$/;
  if(props?.isPhoneNumber) result['validate'] = v => !v || isValidPhoneNumber(v, 'US') === true;
  return result;
};

FormInput.propTypes = {
  disabled: PropTypes.bool,
  errorPositionAfterInput: PropTypes.bool,
  formId: PropTypes.string.isRequired,
  initialValue: PropTypes.any,
  isEmail: PropTypes.bool,
  isPhoneNumber: PropTypes.bool,
  matchPattern: PropTypes.any,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  minLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  timeout: PropTypes.number,
  required: PropTypes.bool,
  type: PropTypes.string,
  options: PropTypes.array,
};

export default FormInput;
